// @flow

import _ from 'lodash/fp';
import React from 'react';
import Select from 'react-select';
import classNames from 'classnames';
import Checkbox from '../Checkbox/Checkbox';
import ReactTooltip from 'react-tooltip';
import './RelatedVariantsInput.css';

export type VariantProps = {
  variant: Object,
  variantSelected: Function,
  productType: Object,
  hasVariantDiffs: boolean,
  isMain: boolean,
};

const Variant = ({
  variant,
  variantSelected,
  productType,
  hasVariantDiffs,
  isMain,
}: VariantProps) => (
  <div className="row item">
    <div className="small-2 column">
      <div
        data-tip
        data-for={`checked-disabled-tip-${variant.id}`}>
        <Checkbox
          checked={variant.selected}
          disabled={!!hasVariantDiffs || !!isMain}
          onChange={e => variantSelected(variant.id, productType, e.target.checked)} />
        {!!isMain && (
          <ReactTooltip
            id={`checked-disabled-tip-${variant.id}`}
            effect="solid">
            <span>Kan ej välja bort varianten då det är en visningsartikel</span>
          </ReactTooltip>
        )}
      </div>
    </div>
    <div className="small-6 column">
      {variant.name}
    </div>
    <div className="small-4 column">
      {variant.itemNumber}
    </div>
  </div>
);

export type VariantListProps = {
  product: Object,
  variantSelected: Function,
  productType: Object,
  hasVariantDiffs: boolean,
};

const VariantList = ({
  product,
  variantSelected,
  productType,
  hasVariantDiffs,
}: VariantListProps) => (
  <div className="row variant-row">
    <div className="small-8 small-offset-4 column variant-grid">
      <div className="row header">
        <div className="small-8 column">
          Produktvariant
        </div>
        <div className="small-4 column">
          Artikelnummer
        </div>
      </div>
      {_.map(variant => (
        <Variant
          key={variant.id}
          productType={productType}
          variant={variant}
          isMain={product.mainVariantId === variant.id}
          variantSelected={variantSelected}
          hasVariantDiffs={hasVariantDiffs} />
      ), product.variants)}
    </div>
  </div>
);

export type ProductProps = {
  product: Object,
  productSelected: Function,
  variantSelected: Function,
  productToggle: Function,
  productType: Object,
  hasVariantDiffs: boolean,
};

const Product = ({
  product,
  productSelected,
  variantSelected,
  productToggle,
  productType,
  hasVariantDiffs,
}: ProductProps) => (
  <div className="row product">
    <div className="column">
      <div
        className="row item"
        onClick={e => {
          if (!!e.target && e.target.type === 'checkbox') {
            return;
          }
          productToggle(product.id);
        }}>
        <div className="small-1 small-offset-1 column">
          <i className={`fa fa-chevron-${!!product.opened ? 'down' : 'right'}`} />
        </div>
        <div className="small-1 column">
          <Checkbox
            checked={product.selected}
            disabled={!!hasVariantDiffs}
            onChange={e => productSelected(product.id, productType, e.target.checked)} />
        </div>
        <div className="small-9 column name-column">
          {product.name}
        </div>
      </div>
      {!!product.opened && (
        <VariantList
          productType={productType}
          product={product}
          variantSelected={variantSelected}
          hasVariantDiffs={hasVariantDiffs} />
      )}
    </div>
  </div>
);

export type ProductTypeProps = {
  productType: Object,
  productSelected: Function,
  variantSelected: Function,
  productToggle: Function,
  productTypeToggle: Function,
  productTypeSelected: Function,
  removeProductType: Function,
  hasVariantDiffs: boolean,
};

const ProductType = ({
  productType,
  productSelected,
  variantSelected,
  productToggle,
  productTypeToggle,
  productTypeSelected,
  removeProductType,
  hasVariantDiffs,
}: ProductTypeProps) => (
  <div className="row product-type">
    <div className="column">
      <div
        className="row item"
        onClick={e => {
          if (!!e.target && (
            e.target.type === 'checkbox' ||
            e.target.type === 'button' ||
            (e.target.localName === 'i' && !!e.target.parentNode && e.target.parentNode.type === 'button')
          )) {
            return;
          }
          productTypeToggle(productType.id);
        }}>
        <div className="small-1 column">
          <i className={`fa fa-chevron-${!!productType.opened ? 'down' : 'right'}`} />
        </div>
        <div className="small-1 column">
          <Checkbox
            checked={productType.selected}
            disabled={!!hasVariantDiffs}
            onChange={e => productTypeSelected(productType, e.target.checked)} />
        </div>
        <div className="small-5 column">
          {productType.name}
        </div>
        <div className="small-4 column">
          {productType.selectedCount}
        </div>
        <div className="small-1 column">
          <button
            type="button"
            className="button small"
            disabled={!!hasVariantDiffs}
            onClick={() => removeProductType(productType.id)}>
            <i className="fa fa-trash fa-lg" />
          </button>
        </div>
      </div>
      {productType.opened && !!productType.products.length && (
        <div className="row header">
          <div className="small-2 small-offset-1 column">
            Tillgänglig
          </div>
          <div className="small-9 column">
            Produkt
          </div>
        </div>
      )}
      {productType.opened && !!productType.products.length && _.map(product => (
        <Product
          key={product.id}
          product={product}
          productType={productType}
          productSelected={productSelected}
          variantSelected={variantSelected}
          productToggle={productToggle}
          hasVariantDiffs={hasVariantDiffs} />
      ), productType.products)}
    </div>
  </div>
);

export type ProductTypeListProps = {
  productSelected: Function,
  variantSelected: Function,
  productToggle: Function,
  productTypeToggle: Function,
  related: any[],
  productTypeSelected: Function,
  removeProductType: Function,
  hasVariantDiffs: boolean,
};

const ProductTypeList = ({
  productSelected,
  variantSelected,
  productToggle,
  productTypeToggle,
  related,
  productTypeSelected,
  removeProductType,
  hasVariantDiffs,
}: ProductTypeListProps) =>
  !!related.length ? (
    <div className="related">
      <div className="row header">
        <div className="small-5 small-offset-2 column">
          Produkttyp
        </div>
        <div className="small-5 column">
          Antal valda varianter
        </div>
      </div>
      {_.map(productType => (
        <ProductType
          key={productType.id}
          productType={productType}
          productSelected={productSelected}
          variantSelected={variantSelected}
          productToggle={productToggle}
          productTypeToggle={productTypeToggle}
          productTypeSelected={productTypeSelected}
          removeProductType={removeProductType}
          hasVariantDiffs={hasVariantDiffs} />
      ), related)}
    </div>
  ) : null;

export type RelatedVariantsInputProps = {
  input: any,
  label: string,
  meta: any,
  productTypes: any[],
  relatedProductType: ?{ id: number },
  setRelatedProductType: Function,
  addRelatedProductType: Function,
  productSelected: Function,
  variantSelected: Function,
  productToggle: Function,
  productTypeToggle: Function,
  related: any[],
  productTypeSelected: Function,
  removeProductType: Function,
  unlockShared: ?Function,
  hasVariantDiffs: boolean,
};

const RelatedVariantsInput = ({
  input,
  label,
  meta: {touched, error},
  productTypes,
  relatedProductType,
  setRelatedProductType,
  addRelatedProductType,
  productSelected,
  variantSelected,
  productToggle,
  productTypeToggle,
  related,
  productTypeSelected,
  removeProductType,
  unlockShared,
  hasVariantDiffs,
}: RelatedVariantsInputProps) => (
  <div
    className="RelatedVariantsInput"
    data-tip
    data-tip-disable={!hasVariantDiffs}
    data-for="variant-diffs-tip">
    <label className={classNames({
      'is-invalid-label': touched && error,
      'related-variant-label': true,
    })}>
      {label}
    </label>
    <div className="row">
      <div className="small-6 column">
        <Select
          value={relatedProductType}
          onChange={setRelatedProductType}
          options={productTypes}
          clearable={false}
          disabled={!!hasVariantDiffs}
          labelKey="name"
          valueKey="id"
          noResultsText="Inga produkttyper hittades" />
      </div>
      <div className="small-6 column">
        <button
          type="button"
          className="button small expanded"
          disabled={!!hasVariantDiffs}
          onClick={() => addRelatedProductType(relatedProductType)}>
          Lägg till
        </button>
      </div>
    </div>
    <ProductTypeList
      productSelected={productSelected}
      variantSelected={variantSelected}
      productToggle={productToggle}
      productTypeToggle={productTypeToggle}
      related={related}
      productTypeSelected={productTypeSelected}
      removeProductType={removeProductType}
      hasVariantDiffs={hasVariantDiffs} />
    {touched && error &&
    <div className="row">
      <span className="form-error is-visible">{error}</span>
    </div>
    }
    {!!hasVariantDiffs && (
      <div className="row unlock-button">
        <div className="small-4 small-offset-4 column end">
          <button
            type="button"
            className="button small expanded"
            onClick={unlockShared}>
            Lås upp
          </button>
        </div>
      </div>
    )}
    {!!unlockShared && (
      <ReactTooltip
        id="variant-diffs-tip"
        effect="solid">
        <span>Det finns skillnader mellan varianterna, tryck på Lås upp för att tillåta ändringar</span>
      </ReactTooltip>
    )}
  </div>
);

export default RelatedVariantsInput;



// WEBPACK FOOTER //
// ./src/components/RelatedVariantsInput/RelatedVariantsInput.js